/**
 * Authentication utilities for cookie-based token management
 * Uses AWS Amplify's cookieStorage for secure token handling
 */

import { Auth } from 'aws-amplify';
import config from '../config.json';

/**
 * Get the current access token from Amplify session
 * @returns {Promise<string|null>} Access token or null if not authenticated
 */
export const getAccessToken = async () => {
  try {
    const session = await Auth.currentSession();
    return session.getAccessToken().getJwtToken();
  } catch (error) {
    console.error('Error getting access token:', error);
    return null;
  }
};

/**
 * Get the current refresh token from Amplify session
 * @returns {Promise<string|null>} Refresh token or null if not authenticated
 */
export const getRefreshToken = async () => {
  try {
    const session = await Auth.currentSession();
    return session.getRefreshToken().getToken();
  } catch (error) {
    console.error('Error getting refresh token:', error);
    return null;
  }
};

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>} True if authenticated
 */
export const isAuthenticated = async () => {
  try {
    await Auth.currentAuthenticatedUser();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get current session (for token refresh)
 * @returns {Promise<object|null>} Session object or null
 */
export const getCurrentSession = async () => {
  try {
    return await Auth.currentSession({ bypassCache: true });
  } catch (error) {
    return null;
  }
};

/**
 * Clear a specific cookie by name across all possible domain/path combinations
 * @param {string} cookieName - Name of the cookie to clear
 */
const clearCookieByName = (cookieName) => {
  try {
    const isLocalhost = Boolean(
      window.location.hostname === "localhost" ||
      window.location.hostname === "[::1]" ||
      window.location.hostname.match(
        /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
      )
    );

    const domains = isLocalhost
      ? ['localhost', window.location.hostname, '']
      : ['.datarate.io', 'www.app.datarate.io', 'app.datarate.io', 'datarate.io', ''];

    const paths = ['/', window.location.pathname];
    const expiredDate = 'Thu, 01 Jan 1970 00:00:00 UTC';
    // Use both Max-Age=0 and expires for maximum browser compatibility
    const secureSuffix = isLocalhost ? '' : '; Secure; SameSite=Lax';

    // Clear cookie with all possible domain/path combinations
    domains.forEach(domain => {
      paths.forEach(path => {
        if (domain) {
          document.cookie = `${cookieName}=; expires=${expiredDate}; Max-Age=0; path=${path}; domain=${domain}${secureSuffix};`;
        }
        // Also try without explicit domain (matches exact hostname)
        document.cookie = `${cookieName}=; expires=${expiredDate}; Max-Age=0; path=${path}${secureSuffix};`;
      });
    });
  } catch (error) {
    console.error(`Error clearing cookie ${cookieName}:`, error);
  }
};

/**
 * Remove unnecessary cookies after login
 * Keeps only: accessToken, refreshToken, idToken, LastAuthUser, clockDrift
 * Removes: userData (heavy JSON), amplify flags
 * NOTE: We keep idToken because Amplify may need it for session management
 */
export const cleanupUnnecessaryCookies = async () => {
  try {
    const appClientId = config.cognito.APP_CLIENT_ID;
    
    // Get current user to identify which cookies to keep
    let currentUsername = null;
    try {
      const user = await Auth.currentAuthenticatedUser();
      currentUsername = user.username;
    } catch (error) {
      // User not authenticated, skip cleanup
      return;
    }
    
    // Get all current cookies
    const allCookies = document.cookie.split(';').map(c => c.trim());
    
    // Find cookies to remove (unnecessary ones)
    const cookiesToRemove = [];
    
    allCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();

      // Remove ALL userData cookies for any user (contains heavy JSON data)
      // This cleans up previous logins' cookies that cause 431 errors
      if (cookieName.startsWith(`CognitoIdentityServiceProvider.${appClientId}.`) &&
          cookieName.endsWith('.userData')) {
        cookiesToRemove.push(cookieName);
      }

      // Remove amplify flags (not needed after login)
      if (cookieName === 'amplify-redirected-from-hosted-ui' ||
          cookieName === 'amplify-signin-with-hostedUI') {
        cookiesToRemove.push(cookieName);
      }
    });
    
    // Remove unnecessary cookies
    cookiesToRemove.forEach(cookieName => {
      clearCookieByName(cookieName);
    });
    
    if (cookiesToRemove.length > 0) {
      console.log(`Cleaned up ${cookiesToRemove.length} unnecessary cookies:`, cookiesToRemove);
    }
  } catch (error) {
    console.error('Error cleaning up unnecessary cookies:', error);
  }
};

/**
 * Clear all Cognito and Amplify cookies
 * This function clears cookies for ALL users, not just the current one
 * This prevents accumulation of old user cookies that cause huge headers
 */
export const clearAllCognitoCookies = () => {
  try {
    const isLocalhost = Boolean(
      window.location.hostname === "localhost" ||
      window.location.hostname === "[::1]" ||
      window.location.hostname.match(
        /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
      )
    );

    const domains = isLocalhost 
      ? ['localhost', window.location.hostname, ''] 
      : ['.datarate.io', 'www.app.datarate.io', 'app.datarate.io', 'datarate.io', ''];
    
    const paths = ['/', window.location.pathname];
    const appClientId = config.cognito.APP_CLIENT_ID;
    
    // Get all current cookies
    const allCookies = document.cookie.split(';').map(c => c.trim());
    
    // Find all Cognito and Amplify cookies
    const cookiesToClear = [];
    
    allCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0];
      if (
        cookieName.startsWith(`CognitoIdentityServiceProvider.${appClientId}.`) ||
        cookieName === `CognitoIdentityServiceProvider.${appClientId}.LastAuthUser` ||
        cookieName.startsWith('amplify-') ||
        cookieName === 'amplify-redirected-from-hosted-ui' ||
        cookieName === 'amplify-signin-with-hostedUI'
      ) {
        cookiesToClear.push(cookieName);
      }
    });
    
    // Clear each cookie with all possible domain/path combinations
    cookiesToClear.forEach(cookieName => {
      clearCookieByName(cookieName);
    });
    
    if (cookiesToClear.length > 0) {
      console.log(`Cleared ${cookiesToClear.length} Cognito/Amplify cookies:`, cookiesToClear);
    }
  } catch (error) {
    console.error('Error clearing Cognito cookies:', error);
  }
};

/**
 * Sign out user and clear all session data
 * Now also clears all Cognito cookies to prevent accumulation
 * Also redirects to Google logout to clear Google OAuth session
 */
export const signOut = async (redirectToLogin = false) => {
  try {
    // Clear all Cognito cookies before signout
    clearAllCognitoCookies();

    // Sign out from Amplify (may re-create some cookies internally)
    await Auth.signOut({ global: true });

    // Clear again after Amplify signOut to remove any cookies it re-created
    clearAllCognitoCookies();

    // Clear profile data
    clearStoredProfile();

    // Also clear localStorage items
    localStorage.removeItem('user_profile');
    localStorage.removeItem('is_extension_login');
    
    // Also sign out from Google OAuth to force account selection on next login
    // This ensures Google's session is cleared so account selection will show
    if (redirectToLogin) {
      const isLocalhost = Boolean(
        window.location.hostname === "localhost" ||
        window.location.hostname === "[::1]" ||
        window.location.hostname.match(
          /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
        )
      );
      
      const loginUrl = isLocalhost 
        ? "http://localhost:3000/login" 
        : "https://www.app.datarate.io/login";
      
      try {
        // Get Cognito config for logout URL
        const amplifyConfig = Auth.configure();
        const domain = Array.isArray(amplifyConfig.oauth?.domain) 
          ? amplifyConfig.oauth.domain[0] 
          : amplifyConfig.oauth?.domain;
        const clientId = amplifyConfig.userPoolWebClientId;
        
        if (domain && clientId) {
          // Use Cognito's logout endpoint which properly handles the logout flow
          // This will clear Cognito session and redirect to login page
          const cognitoLogoutUrl = `https://${domain}/logout?` +
            `client_id=${clientId}&` +
            `logout_uri=${encodeURIComponent(loginUrl)}`;
          
          // Redirect to Cognito logout
          window.location.href = cognitoLogoutUrl;
          return; // Exit early since we're redirecting
        } else {
          // Fallback: just navigate to login page
          window.location.href = loginUrl;
          return;
        }
      } catch (googleLogoutError) {
        console.warn('Could not redirect to Google logout:', googleLogoutError);
        // Fallback: navigate to login page
        window.location.href = loginUrl;
        return;
      }
    }
  } catch (error) {
    console.error('Error signing out:', error);
    // Even if signOut fails, still clear cookies
    clearAllCognitoCookies();
  }
};

/**
 * Sanitize user profile data for client-side storage
 * Removes sensitive fields that should not be stored in localStorage/sessionStorage
 * @param {Object} profile - Full user profile object
 * @returns {Object} Sanitized profile with only safe display data
 */
export const sanitizeProfileForStorage = (profile) => {
  if (!profile || typeof profile !== 'object') {
    return null;
  }

  // Fields that are safe to store (public display data only)
  const safeFields = [
    'id',
    'user_id',
    'user_name',
    'first_name',
    'last_name',
    'profile_image_url',
    'tags',
    'most_interested',
    'points',
    'level',
    'level_title',
    'title',
    'allow_to_show_in_top_users',
  ];

  // Create sanitized profile with only safe fields
  const sanitized = {};
  safeFields.forEach(field => {
    if (profile[field] !== undefined) {
      sanitized[field] = profile[field];
    }
  });

  return sanitized;
};

/**
 * Store user profile in sessionStorage (cleared on tab close)
 * Only stores non-sensitive display data
 * @param {Object} profile - Full user profile object
 */
export const storeProfileSafely = (profile) => {
  try {
    const sanitized = sanitizeProfileForStorage(profile);
    if (sanitized) {
      // Use sessionStorage instead of localStorage for better security
      // sessionStorage is cleared when tab closes, reducing XSS impact
      sessionStorage.setItem('user_profile', JSON.stringify(sanitized));
    }
  } catch (error) {
    console.error('Error storing profile:', error);
  }
};

/**
 * Get stored profile from sessionStorage
 * @returns {Object|null} Stored profile or null
 */
export const getStoredProfile = () => {
  try {
    const stored = sessionStorage.getItem('user_profile');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading stored profile:', error);
  }
  return null;
};

/**
 * Clear stored profile from sessionStorage
 */
export const clearStoredProfile = () => {
  try {
    sessionStorage.removeItem('user_profile');
  } catch (error) {
    console.error('Error clearing stored profile:', error);
  }
};